import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { v4 as uuidv4 } from "uuid";
import authBgImage from "../../assets/image/authBgImage.jpeg";
import LoginIcon from "../../assets/image/logo.png";
import ButtonLoader from "../../components/loader/ButtonLoader";
import { loginApiAdmin } from "../../redux/auth/slice";
import RippleLoader from "../../components/RippleLoader/RippleLoader";
import { TextField } from "@mui/material";

// Utility functions
const getDeviceType = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/mobile/i.test(userAgent)) return "Mobile";
  else if (/tablet|ipad|playbook|silk|android(?!.*mobi)/i.test(userAgent))
    return "Tablet";
  return "Desktop";
};
const getDeviceName = () => {
  const userAgent = navigator.userAgent;
  if (userAgent.match(/iPhone/i)) return "iPhone";
  if (userAgent.match(/iPad/i)) return "iPad";
  if (userAgent.match(/Android/i)) return "Android Device";
  if (userAgent.match(/Windows/i)) return "Windows PC";
  if (userAgent.match(/Mac/i)) return "Mac";
  return "Unknown Device";
};
const getDeviceID = () => {
  let deviceID = localStorage.getItem("device_id");
  if (!deviceID) {
    deviceID = uuidv4();
    localStorage.setItem("device_id", deviceID);
  }
  return deviceID;
};

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loginState, setLoginState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deviceType, setDeviceType] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [deviceID, setDeviceID] = useState("");
  const storedFcmToken = localStorage.getItem("fcm_token");

  const { logingData, logingDataLoading, logingDataError } = useSelector(
    (store) => ({
      logingData: store?.authData?.loginStatus?.data?.data?.data,
      logingDataLoading: store?.authData?.loading,
      logingDataError: store?.authData?.error,
    })
  );

  useEffect(() => {
    if (!logingDataLoading && !logingDataError && loginState) {
      logingData ? navigate("/") : navigate("/");
    } else {
      navigate("/login");
    }
  }, [navigate, logingDataLoading, logingDataError, loginState, logingData]);

  useEffect(() => {
    setDeviceType(getDeviceType());
    setDeviceName(getDeviceName());
    setDeviceID(getDeviceID());
  }, [dispatch]);

  const handleLogin = (values) => {
    dispatch(
      loginApiAdmin({
        fcm_token: storedFcmToken,
        device_type: deviceType,
        device_name: deviceName,
        device_id: deviceID,
        is_client: 1,
        ...values,
      })
    ).then((res) => {
      setLoginState(false);
      setLoading(false);
      if (res?.type === "loginApiAdmin/fulfilled") {
        setLoginState(true);
        navigate("/");
      }
    });
  };

  const handleRememberMeChange = (newValue, values) => {
    if (newValue) {
      localStorage.setItem("authEmail", values.email);
      localStorage.setItem("authPassword", values.password);
    } else {
      localStorage.removeItem("authEmail");
      localStorage.removeItem("authPassword");
      localStorage.removeItem("authToken");
    }
  };

  const storedEmail = localStorage.getItem("authEmail");
  const storedPassword = localStorage.getItem("authPassword");
  const storedRememberMe = storedEmail && storedPassword;

  return (
    <>
      <div className="h-screen w-full relative">
        {logingDataLoading && <RippleLoader />}
        <div
          className="flex items-center justify-center h-full bg-no-repeat bg-cover bg-center bg-black/90"
          style={{ backgroundImage: `url(${authBgImage})` }}
        >
          <section className="w-full px-4">
            <div className="max-w-xl w-full mx-auto">
              <div className="rounded-lg shadow-xl bg-white/90 px-4 py-8 sm:px-8 sm:py-10 md:py-12 md:px-10">
                <div className="pb-6 text-center md:text-left">
                  <img
                    src={LoginIcon}
                    alt="logo"
                    className="mx-auto md:mx-0 w-32 md:w-52"
                  />
                </div>

                <div className="mb-6 text-center md:text-left">
                  <h4 className="text-2xl md:text-[34px]  text-gray-900 mb-1 font-Montserrat">
                    Welcome!
                  </h4>
                  <p className="text-sm md:text-base text-gray-700 font-Proxima">
                    To FBK System
                  </p>
                </div>

                <Formik
                  initialValues={{
                    email: storedEmail || "",
                    password: storedPassword || "",
                    rememberMe: storedRememberMe || false,
                  }}
                  validationSchema={Yup.object({
                    email: Yup.string().required("Please enter email"),
                    password: Yup.string()
                      .required("Please enter password")
                      .min(
                        8,
                        "Password is too short - should be 8 chars minimum."
                      )
                      .matches(
                        /[a-zA-Z]/,
                        "Password can only contain Latin letters."
                      ),
                  })}
                  onSubmit={(values) => {
                    handleLogin(values);
                    setLoading(true);
                    setLoginState(true);
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="space-y-6">
                        {/* Email */}
                        <TextField
                          fullWidth
                          color="warning"
                          variant="outlined"
                          id="email"
                          label="Email"
                          type="email"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          error={errors.email && touched.email}
                          helperText={
                            errors.email && touched.email && errors.email
                          }
                          InputLabelProps={{ shrink: !!values.email }}
                        />

                        {/* Password */}
                        <TextField
                          fullWidth
                          variant="outlined"
                          color="warning"
                          id="password"
                          label="Password"
                          type="password"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.password}
                          error={errors.password && touched.password}
                          helperText={
                            errors.password &&
                            touched.password &&
                            errors.password
                          }
                          InputLabelProps={{ shrink: !!values.password }}
                        />

                        {/* Remember Me and Forgot Password */}
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-0">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              id="rememberMe"
                              className="w-4 h-4 mr-2 accent-themeBtn"
                              checked={values.rememberMe}
                              onChange={() => {
                                const newValue = !values.rememberMe;
                                setFieldValue("rememberMe", newValue);
                                handleRememberMeChange(newValue, values);
                              }}
                            />
                            <span className="text-sm text-gray-800 font-medium font-Proxima">
                              Remember Me
                            </span>
                          </label>

                          <Link
                            to="/reset-password"
                            className="text-sm font-medium text-themeBtn border-b border-lightOrangeTheme font-Proxima"
                          >
                            Forgot password?
                          </Link>
                        </div>

                        {/* Login Button */}
                        <button
                          type="submit"
                          className="w-full bg-themeBtn font-Proxima text-white font-medium text-base py-2 px-4 rounded hover:bg-transparent hover:border hover:border-themeBtn hover:text-themeBtn transition duration-300"
                        >
                          {loading ? <ButtonLoader /> : "LOGIN"}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
              <p className="text-white text-center text-sm mt-4 font-medium font-Proxima">
                FBK Group Pte Ltd
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default Login;
